<template>
    <el-dialog
      v-model="dialogVisible"
      :title="effectiveTitle"
      width="420px"
      class="share-dialog"
    >
      <div class="share-content" v-loading="effectiveUrl === ''">
        <div class="url-section">
          <div class="input-wrap">
            <el-input :model-value="effectiveUrl" size="large" readonly />
            <el-button @click="copyUrl" plain size="large" :disabled="effectiveUrl === ''">Copy</el-button>
          </div>
        </div>

        <ul class="social-buttons">
          <li @click="shareTo">
            <button>
              <svg-icon name="shareto" />
            </button>
            <span>Share</span>
          </li>
          <li @click="shareToEmail">
            <button>
              <svg-icon name="email" />
            </button>
            <span>Email</span>
          </li>
        </ul>
      </div>
    </el-dialog>
  </template>

  <script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import copyToClipboard from 'copy-to-clipboard';
  import { useNotification } from '@shared/components/ui/notification';
  import emitter from '@shared/utils/EventBus';
  const { openNotBottom } = useNotification();

  const props = defineProps({
    url: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: 'Share'
    },
    heading: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    }
  })

  const dialogVisible = ref(false)
  const useInternal = ref(false)
  const internalUrl = ref('')
  const internalTitle = ref('Share')
  const internalHeading = ref('')
  const internalDescription = ref('')

  const effectiveUrl = computed(() => useInternal.value ? internalUrl.value : props.url)
  const effectiveTitle = computed(() => useInternal.value ? internalTitle.value : props.title)
  const effectiveHeading = computed(() => useInternal.value ? internalHeading.value : props.heading)
  const effectiveDescription = computed(() => useInternal.value ? internalDescription.value : props.description)

  const copyUrl = () => {
    if (!effectiveUrl.value) return;
    copyToClipboard(effectiveUrl.value)
    openNotBottom(`Copied to clipboard`, 'success');
  }

  const shareToEmail = () => {
    const share_url = 'mailto:?subject=' + effectiveHeading.value + '&body='+ effectiveDescription.value +' %0A%0A Check out this link at: ' + effectiveUrl.value;
    window.open(share_url, '_blank');
  }

  const shareTo = () => {
    navigator.share({
        title: effectiveHeading.value,
        text: effectiveDescription.value,
        url: effectiveUrl.value
    })
  }

  const open = () => {
    useInternal.value = false
    dialogVisible.value = true
  }

  const onOpenEvent = (payload = {}) => {
    useInternal.value = true
    internalUrl.value = ''
    internalTitle.value = payload.title || 'Share'
    internalHeading.value = payload.heading || ''
    internalDescription.value = payload.description || ''
    dialogVisible.value = true
  }

  const onSetUrlEvent = (payload = {}) => {
    if (!useInternal.value) return
    internalUrl.value = payload.url || ''
  }

  onMounted(() => {
    emitter.on('open-share-popup', onOpenEvent)
    emitter.on('share-popup-set-url', onSetUrlEvent)
  })

  onBeforeUnmount(() => {
    emitter.off('open-share-popup', onOpenEvent)
    emitter.off('share-popup-set-url', onSetUrlEvent)
  })

  defineExpose({
    open
  })
  </script>
  
  <style lang="scss">
  .share-dialog {
    padding: 0 !important;
    overflow: hidden;

    .ui-dialog__header {
      padding: 0 24px;
      display: flex;
      align-items: center;
      height: 48px;

      @media screen and (max-width: $mobile_size) {
        position: sticky;
        top: 0;
        z-index: 9;
        background-color: var(--Bg-light);
        padding: 0 16px;
      }

      .ui-dialog__title {
        flex: 1;
      }

      .ui-dialog__close {
        margin: 0;
      }
    }

    .share-content {
      padding: 24px;
      background-color: var(--Bg-01);
    }
  
    .url-section {
      margin-bottom: 24px;
  
      .input-wrap {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        cursor: pointer;
        
        .copy {
          padding: 0 12px;
          color: var(--Text-default);
        }
  
        .ui-input {
          flex: 1;
          margin-right: 10px;
        }
      }
    }
  
    .social-buttons {
      display: flex;
      justify-content: space-around;
      margin: 0 32px;

      li {
        width: 48px;
        cursor: pointer;

        span {
          display: block;
          text-align: center;
          color: var(--Text-primary);
          padding: 4px 0;
          transition: all 0.3s;
        }

        &:hover {
          color: var(--Text-brand);

          button {
            border-color: var(--Text-brand);
            color: var(--Text-brand);
          }

        }
      }

      button {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: 1px solid var(--Border-default);
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: var(--Bg-light);
        color: var(--Text-primary);


        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
  </style>